import { Close, ExpandMore, Search } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Checkbox,
    Divider,
    Button,
    Drawer,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import React, { useEffect } from 'react';
import * as PXBColors from '@brightlayer-ui/colors';
import { useLanguageLocale } from '../../../hooks/language-locale-hooks';
import manageAlarmsStyle from '../ManageAlarmsStyle';
import {
    AlarmConfigData,
    ChannelDetails,
    ManageAdvAlarmConfig,
    ManageAlarmConfig,
    ManageAlarmsTabsEnum,
    ManageDigitalAlarmConfig,
    isDataLoaded,
} from '../../../types';
import { ConfigureAlarms } from './ConfigureAlarms';
import { ConfigureAdvAlarms } from './ConfigureAdvAlarms';
import { useAlarmConfigData } from '../../../data/DataProviders/AlarmConfigProvider';
import { Thunks } from '../../../actions';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from '../../../data/DataProviders/ToastProvider';
import { useAuthHelper } from '../../AuthContextProvider';
import { Selectors } from '../../../selectors';
import { DataIndicators } from '../../DataIndicators';
import { ConfigureDigitalAlarms } from './ConfigureDigitalAlarms';

const AlarmsStyleTabs = withStyles({
    root: {
        color: 'black',
        height: 28,
        '& .contentBox.MuiTabs-root': {
            left: '25px',
        },
        '& .MuiTab-textColorPrimary.Mui-selected': {
            color: '#1976d2',
        },
        '& .MuiTab-textColorPrimary': {
            color: 'rgba(0, 0, 0, 0.54)',
        },
        '& .MuiTabs-indicator': {
            backgroundColor: '#1976d2',
        },
    },
})(Tabs);

export const AlarmConfigDrawer: React.FC<{
    open: boolean;
    deviceId: string;
    openDrawer: Function;
    channelDetails: ChannelDetails;
    closeDrawer: any;
}> = (props) => {
    const alarmConfigData = useAlarmConfigData();
    const [channelDetails, setChannelDetails] = React.useState(props.channelDetails);
    const [alarmConfig, setAlarmConfig] = React.useState<AlarmConfigData | null>(null);
    const [updateAlarmConfig, setUpdateAlarmConfig] = React.useState<ManageAlarmConfig | null>(null);
    const [updateAdvAlarmConfig, setUpdateAdvAlarmConfig] = React.useState<ManageAlarmConfig | null>(null);
    const [updateDigitalAlarmConfig, setUpdateDigitalAlarmConfig] = React.useState<ManageDigitalAlarmConfig | null>(
        null
    );
    const manageAlarmState = useSelector(Selectors.ManageAlarmsSelectors.filtersData);
    const [contextualDataId, setContextualDataId] = React.useState('');
    const dispatch = useDispatch();
    const { addToast } = useToasts();
    const authHelper = useAuthHelper();
    const classes = manageAlarmsStyle();

    useEffect(() => {
        if (props.deviceId !== '') alarmConfigData.getAlarmConfigData(props.deviceId, props.channelDetails.tagId);
    }, [props]);

    useEffect(() => {
        if (isDataLoaded(alarmConfigData.alarmConfig) && alarmConfigData) {
            setAlarmConfig(alarmConfigData.alarmConfig.value);
            if (alarmConfigData.alarmConfig.value.alarmConfigurations.length > 0)
                setContextualDataId(alarmConfigData.alarmConfig.value.alarmConfigurations[0].contextualDataId); //Saved Config
        }
    }, [alarmConfigData]);

    useEffect(() => {
        if (props.channelDetails) setChannelDetails(props.channelDetails);
    }, [props.channelDetails]);

    const [value, setValue] = React.useState(0);
    const { t } = useLanguageLocale();

    function tabProps(index: number): any {
        return {
            id: `alarms-config-drawer-${index}`,
        };
    }

    const updateAlarmConfigData = (modifiedData: ManageAlarmConfig): void => {
        setUpdateAlarmConfig(modifiedData);
    };

    const updateAdvAlarmData = (modifiedData: ManageAlarmConfig): void => {
        setUpdateAdvAlarmConfig(modifiedData);
    };

    const updateDigitalAlarmData = (modifiedData: ManageDigitalAlarmConfig): void => {
        setUpdateDigitalAlarmConfig(modifiedData);
    };

    const initAlarmConfig = (
        config: ManageAlarmConfig | ManageDigitalAlarmConfig,
        deviceId: string,
        channels: ChannelDetails,
        onlyAdv = false
    ): void => {
        const newAlarmConfig: any = { ...config };
        newAlarmConfig.deviceId = deviceId;
        newAlarmConfig.tagId = channels.tagId.toString();
        newAlarmConfig.tagName = channels.name;
        newAlarmConfig.active = true;
        newAlarmConfig.requestedFrom = 'Remote';
        if (onlyAdv) {
            newAlarmConfig.latching = false;
            newAlarmConfig.priority = 1;
            newAlarmConfig.type = '';
            newAlarmConfig.highCritical = {
                isChecked: false,
                limit: 0,
                message: '',
            };
            newAlarmConfig.lowCritical = {
                isChecked: false,
                limit: 0,
                message: '',
            };
            newAlarmConfig.highCaution = {
                isChecked: false,
                limit: 0,
                message: '',
            };
            newAlarmConfig.lowCaution = {
                isChecked: false,
                limit: 0,
                message: '',
            };
        }
        return newAlarmConfig;
    };

    const updateAdvConfig = (_updateAdvAlarmConfig: ManageAlarmConfig | null, newAlarmConfig: any): void => {
        if (_updateAdvAlarmConfig) {
            const advAlarmConfig: ManageAdvAlarmConfig = { ..._updateAdvAlarmConfig };
            newAlarmConfig.ackHoldOff = advAlarmConfig.ackHoldOff;
            newAlarmConfig.alarmDelay = advAlarmConfig.alarmDelay;
            newAlarmConfig.rearm = advAlarmConfig.rearm;
            newAlarmConfig.latching = advAlarmConfig.latching;
        }
    };
    const onSubmit = (): void => {
        let newAlarmConfig: any = {};

        if (channelDetails.type === 'Digital') {
            if (updateDigitalAlarmConfig) {
                // Only Digital + Adv
                newAlarmConfig = initAlarmConfig(updateDigitalAlarmConfig, props.deviceId, channelDetails);
                updateAdvConfig(updateAdvAlarmConfig, newAlarmConfig);
            } else {
                // Only Adv
                if (updateAdvAlarmConfig) {
                    newAlarmConfig = initAlarmConfig(updateAdvAlarmConfig, props.deviceId, channelDetails, true);
                    updateAdvConfig(updateAdvAlarmConfig, newAlarmConfig);
                }
            }
        }
        if (channelDetails.type === 'Analog') {
            if (updateAlarmConfig) {
                //Only Analog + Adv
                newAlarmConfig = initAlarmConfig(updateAlarmConfig, props.deviceId, channelDetails);
                updateAdvConfig(updateAdvAlarmConfig, newAlarmConfig);
            } else {
                // Only Adv
                if (updateAdvAlarmConfig) {
                    newAlarmConfig = initAlarmConfig(updateAdvAlarmConfig, props.deviceId, channelDetails, true);
                    updateAdvConfig(updateAdvAlarmConfig, newAlarmConfig);
                }
            }
        }

        if (contextualDataId && contextualDataId !== '') {
            const alarmConfigArray: any[] = [];
            const alarmConfigurationsToUpdate = {
                contextualDataId: contextualDataId,
                alarmConfiguration: newAlarmConfig,
            };
            alarmConfigArray.push(alarmConfigurationsToUpdate);

            dispatch(
                Thunks.ManageAlarmsThunks.UpdateAlarmConfig(
                    manageAlarmState.filters.gatewayId,
                    alarmConfigArray,
                    authHelper,
                    addToast
                )
            );
        } else {
            const alarmConfigArray: ManageAlarmConfig[] = [];
            alarmConfigArray.push(newAlarmConfig);
            dispatch(
                Thunks.ManageAlarmsThunks.SaveAlarmConfig(
                    manageAlarmState.filters.gatewayId,
                    alarmConfigArray,
                    authHelper,
                    addToast
                )
            );
        }

        props.closeDrawer();
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number): any => {
        setValue(newValue);
    };
    return (
        <>
            <Drawer
                anchor="right"
                sx={{ mt: 10, mb: 10 }}
                open={props.open}
                onClose={props.closeDrawer}
                id={props.channelDetails.name}
                key={props.channelDetails.name}
                classes={{
                    paper: classes.drawerPaper,
                }}
            >
                <Box sx={{ width: '450px', height: '600px' }}>
                    <Box style={{ padding: 8, display: 'flex' }}>
                        <Typography style={{ flex: '1 1 auto', margin: 16, color: PXBColors.blue[500] }} variant="h6">
                            {channelDetails.name}
                        </Typography>
                        <IconButton
                            style={{ right: 0, color: PXBColors.gray[500] }}
                            onClick={(): void => props.closeDrawer()}
                        >
                            <Close />
                        </IconButton>
                    </Box>{' '}
                    <Divider></Divider>
                    {manageAlarmState.filters.type === ManageAlarmsTabsEnum.bulkalarms && (
                        <Accordion>
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                            >
                                <Typography style={{ lineHeight: 1 }} variant="subtitle2">
                                    Devices
                                </Typography>
                                <Typography style={{ marginLeft: -50, marginTop: 15 }} variant="body1">
                                    3 of 4
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box className={classes.paddingBottom8}>
                                    <TextField
                                        inputProps={{ 'data-testid': 'Search Device' }}
                                        fullWidth
                                        variant="filled"
                                        id="search_device"
                                        label=""
                                        placeholder="Search.. "
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search data-testid="search-icon" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {
                                                        <IconButton size="large">
                                                            <Close fontSize="small" />
                                                        </IconButton>
                                                    }
                                                </InputAdornment>
                                            ),
                                        }}
                                        // onChange={(e): void => {
                                        //     onTextChange(e);
                                        // }}
                                    />
                                </Box>
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                sx={{ color: PXBColors.blue[500] }}
                                                checked={true}
                                                data-testid="gi-critical"
                                                // onChange={(evt): void => setRemember(evt.target.checked)}
                                            />
                                        }
                                        label="All"
                                    />
                                </Box>
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                sx={{ color: PXBColors.blue[500] }}
                                                checked={true}
                                                data-testid="gi-critical"
                                                // onChange={(evt): void => setRemember(evt.target.checked)}
                                            />
                                        }
                                        label="PXM2290"
                                    />
                                </Box>
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                sx={{ color: PXBColors.blue[500] }}
                                                checked={true}
                                                data-testid="gi-critical"
                                                // onChange={(evt): void => setRemember(evt.target.checked)}
                                            />
                                        }
                                        label="PXM 2260"
                                    />
                                </Box>
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                sx={{ color: PXBColors.blue[500] }}
                                                checked={true}
                                                data-testid="gi-critical"
                                                // onChange={(evt): void => setRemember(evt.target.checked)}
                                            />
                                        }
                                        label="PXM400"
                                    />
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )}
                    <Box sx={{ display: 'flex' }}>
                        <AlarmsStyleTabs
                            indicatorColor="primary"
                            textColor="primary"
                            style={{ marginLeft: '0px', flex: '1 1 auto' }}
                            value={value}
                            onChange={handleTabChange}
                            aria-label=""
                            variant="fullWidth"
                        >
                            <Tab label={t('MANAGEALARMS.ALARMTABLABEL')} {...tabProps(0)} />
                            <Tab label={t('MANAGEALARMS.ADVANCEDTABLABEL')} {...tabProps(1)} />
                        </AlarmsStyleTabs>
                    </Box>
                    <Box>
                        <Box style={{ height: '62vh' }}>
                            {!isDataLoaded(alarmConfigData.alarmConfig) && (
                                <DataIndicators
                                    errorMessage="Alarm Configuration not Found"
                                    emptyMessage="alarmConfigData"
                                    inline
                                    data={alarmConfigData.alarmConfig}
                                    emptyIcon="Devices"
                                    isEmpty={false}
                                    isDetailedInfo={false}
                                />
                            )}
                            {isDataLoaded(alarmConfigData.alarmConfig) && alarmConfig && (
                                <Box>
                                    {channelDetails.type === 'Analog' && (
                                        <ConfigureAlarms
                                            value={value}
                                            index={0}
                                            alarmConfigData={alarmConfig}
                                            updateAlarmConfigData={updateAlarmConfigData}
                                        ></ConfigureAlarms>
                                    )}

                                    {channelDetails.type === 'Digital' && (
                                        <ConfigureDigitalAlarms
                                            value={value}
                                            index={0}
                                            alarmConfigData={alarmConfig}
                                            updateDigitalAlarmData={updateDigitalAlarmData}
                                        ></ConfigureDigitalAlarms>
                                    )}

                                    <ConfigureAdvAlarms
                                        value={value}
                                        index={1}
                                        alarmConfigData={alarmConfig}
                                        updateAdvAlarmData={updateAdvAlarmData}
                                    ></ConfigureAdvAlarms>
                                </Box>
                            )}
                        </Box>

                        <Divider></Divider>
                        <Box style={{ display: 'flex', justifyContent: 'flex-end' }} className={classes.marginpadding8}>
                            <Button
                                color="primary"
                                variant="outlined"
                                style={{ marginRight: 20 }}
                                data-testid="cancel-action"
                                onClick={(): void => {
                                    props.closeDrawer();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={false}
                                color="primary"
                                variant="contained"
                                data-testid="submit-action"
                                onClick={onSubmit}
                            >
                                Submit Changes
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};
